<template>
  <div id="lm-searcher">
    <div class="app-search w-100">
      <div
        v-if="isVisibleMoreOptions"
        class="app-search__more-options-content p-3"
        @keydown.esc="closeMoreOptions"
        ref="moreOptionsContent"
        tabindex="-1"
      >
        <div class="row ml-2">
          <div class="col-12 pl-0">
            <h6>{{ $t("search_in") }}</h6>
          </div>
        </div>
        <div class="row ml-2" v-if="searchOnFieldsToSelect.length">
          <div
            class="custom-control custom-checkbox col-md-6 mt-1"
            v-for="(option, index) in searchOnFieldsToSelect"
            :key="index"
          >
            <div v-if="option && option.hasOwnProperty('name')">
              <input
                class="custom-control-input"
                type="checkbox"
                :value="option.name"
                :id="option.name"
                v-model="searchOnFieldsSelected"
              />
              <label class="custom-control-label" :for="option.name">
                {{ keyReadableForHuman(option.humanName) }}
              </label>
            </div>
            <div v-else>
              <input
                class="custom-control-input"
                type="checkbox"
                :value="option"
                :id="option"
                v-model="searchOnFieldsSelected"
              />
              <label class="custom-control-label" :for="option">
                {{ keyReadableForHuman(option) }}
              </label>
            </div>
          </div>
        </div>
        <div class="row mt-3" v-if="showFilterTime">
          <div class="col-12 col-lg-6 mt-2">
            <LmDate
              v-model="dateTime.from"
              :placeholder="$t('select_date')"
              :label="$t('min_date')"
              :config="lmDateConfigFrom"
              id="dateTimeFrom"
            />
          </div>
          <div class="col-12 col-lg-6 mt-2">
            <LmDate
              v-model="dateTime.upTo"
              :placeholder="$t('select_date')"
              :label="$t('max_date')"
              :config="lmDateConfigUpTo"
              id="dateTimeUpTo"
            />
          </div>
        </div>
        <div v-for="(lmSuperPowerSelect, index) in lmSuperPowerSelectsV2" :key="`lmSuperPowerSelect-${index}`" class="row">
            <div :class="[{'col-9': lmSuperPowerSelect.multiple}, {'col-12': !lmSuperPowerSelect.multiple}]">
              <LmSuperPowerSelectV2
                :ref="`lmSuperPowerSelect${index}`"
                :options="lmSuperPowerSelect.options"
                :label="lmSuperPowerSelect.label"
                :placeholder="lmSuperPowerSelect.placeholder"
                :multiple="lmSuperPowerSelect.multiple || false"
                v-model="superPowerOptionsSelected[lmSuperPowerSelect.keyQueryString]"
                @input="updateLmSuperPower(lmSuperPowerSelect.keyQueryString, $event)"
              />
            </div>
            <div class="col-3">
              <LmSelect
                v-if="lmSuperPowerSelect.multiple"
                :value="getLmSelectValueFromtQueryString(lmSuperPowerSelect.keyQueryString || '')"
                :options="{
                  content: [{value: 'or', text: 'Cualquiera'}, {value: 'and', text: 'Todos'}]
                }"
                label="Select"
                @input="updateLmSuperPower(lmSuperPowerSelect.keyQueryString, $event)"
              />
            </div>
        </div>
        <div class="row ml-2">
          <div class="col-5 mt-4 pl-0">
            <button
              @click="cleanOptions"
              v-if="
                searchOnFieldsToSelect.length &&
                  searchOnFieldsSelected.length >= 1
              "
              type="button"
              class="btn btn-link btn-sm pl-0"
            >
              {{ $t("clean_options") }}
            </button>
            <button
              @click="selectAllOptions"
              v-if="
                searchOnFieldsToSelect.length &&
                  searchOnFieldsSelected.length === 0
              "
              type="button"
              class="btn btn-link btn-sm pl-0"
            >
              {{ $t("select_all") }}
            </button>
          </div>
          <div class="col-7 d-flex justify-content-end mt-4">
            <button
              @click="closeMoreOptions"
              type="button"
              class="btn btn-link btn-sm mr-2"
            >
              {{ $t("close") }}
            </button>
            <span
              class="d-inline-block"
              tabindex="0"
            >
              <button
                @click="searchItems"
                type="button"
                class="btn btn-primary btn-sm"
              >
                {{ $t("search") }}
              </button>
            </span>
          </div>
        </div>
      </div>
      <div class="input-group" @keydown.esc="closeMoreOptions">
        <input
          autofocus
          type="text"
          v-model.trim="term"
          class="form-control app-search__main-input"
          @keyup.delete.exact="inputClean"
          @keyup.ctrl.88="inputClean"
          @keyup.ctrl.8="inputClean"
          @keyup.enter.exact="searchItems"
          :placeholder="$t('search_placeholder')"
        />
        <span class="mdi mdi-magnify"></span>
        <div class="input-group-append position-relative">
          <button
            v-if="showMoreOptions"
            class="btn btn-primary"
            type="button"
            @click="moreOptions"
          >
            {{ $t("more_options") }}
          </button>
          <button
            v-if="!showMoreOptions"
            class="btn btn-primary"
            type="button"
            @click="searchItems"
          >
            {{ $t("search") }}
          </button>
          <i class="noti-icon-badge" v-if="areDefaultFiltersChanged" />
        </div>
      </div>
    </div>
  </div>
  <!-- Row -->
</template>

<script>
import urlQueryBuilder from '@/mixins/urlQueryBuilder.js'
import keyReadableForHuman from '@/helpers/key-readable-for-humans.js'
import LmDate from '@/components/form/elements/LmDate'
import LmSuperPowerSelectV2 from '@/components/form/elements/LmSuperPowerSelectV2.vue'
import LmSelect from '@/components/form/elements/LmSelect.vue'
export default {
  mixins: [urlQueryBuilder],
  components: {
    LmDate,
    LmSuperPowerSelectV2,
    LmSelect
  },
  data () {
    return {
      superPowerOptionsSelected: {},
      superPowers: {},
      isVisibleMoreOptions: false,
      loading: true,
      term: '',
      searchOnFieldsSelected: [],
      dateTime: {
        from: null,
        upTo: null
      }
    }
  },
  props: {
    maxDateUpTo: {
      type: String,
      default: '2020-09-30'
    },
    lmSuperPowerSelectsV2: {
      type: Array,
      default: () => []
    },
    dateFromProp: {
      type: String,
      default: ''
    },
    dateUpToProp: {
      type: String,
      default: ''
    },
    searchOnFieldsToSelect: {
      type: Array,
      default: () => []
    },
    searchOnFieldsSelectedProp: {
      type: Array,
      default: () => []
    },
    termProp: {
      type: String,
      default: ''
    },
    showFilterTime: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    areDefaultFiltersChanged () {
      return (
        this.searchOnFieldsSelected.length !==
          this.searchOnFieldsToSelect.length ||
        this.dateTime.from ||
        this.dateTime.upTo
      )
    },
    lmDateConfigFrom () {
      return {
        maxDate: this.dateTime.upTo || this.lmDateConfigUpTo.maxDate
      }
    },
    lmDateConfigUpTo () {
      return {
        maxDate: this.maxDateUpTo || 'today'
      }
    },
    showMoreOptions () {
      return this.searchOnFieldsToSelect.length || this.showFilterTime
    }
  },
  methods: {
    getLmSelectValueFromtQueryString (queryString, event) {
      const superPowers = this.$route.query?.superPowers ? JSON.parse(this.$route.query.superPowers) : {}
      return superPowers?.[queryString]?.relation || this.superPowers?.[queryString]?.relation || 'or'
    },
    updateLmSuperPower (keyQueryString, values) {
      if (this.superPowers[keyQueryString] === undefined) {
        this.superPowers[keyQueryString] = {
          content: [],
          relation: 'or'
        }
      }
      if (typeof values === 'string') {
        this.superPowers[keyQueryString].relation = values
      }
      if (Array.isArray(values) || values?.constructor.name === 'Object') {
        this.superPowers[keyQueryString].content = values
      }
      if (keyQueryString in this.superPowers && Array.isArray(values) && values.length === 0) {
        delete this.superPowers[keyQueryString]
      }
    },
    focus () {
      this.$refs.moreOptionsContent.focus()
    },
    selectAllOptions () {
      this.searchOnFieldsSelected = this.searchOnFieldsToSelect.find(
        i => i.name
      )
        ? this.searchOnFieldsToSelect.map(i => i.name)
        : this.searchOnFieldsToSelect
      this.$refs.moreOptionsContent.focus()
    },
    cleanOptions () {
      this.searchOnFieldsSelected = []
      this.dateTime.from = null
      this.dateTime.upTo = null
      this.$refs.moreOptionsContent.focus()
      Object.keys(this.superPowers).forEach((element, index) => {
        this.$refs[`lmSuperPowerSelect${index}`][0].remove()
        if (this.$refs[`lmSuperPowerSelect${index}`][0].multiple) {
          this.$refs[`lmSuperPowerSelect${index}`][0].hide()
        }
      })
      this.superPowers = {}
    },
    searchItems () {
      this.closeMoreOptions()
      this.urlQueryBuilder({ page: null })
      this.urlQueryBuilder({ term: this.term })
      this.urlQueryBuilder({
        searchOnFields: JSON.stringify(this.searchOnFieldsSelected)
      })
      this.urlQueryBuilder({ dateTimeFrom: this.dateTime.from })
      this.urlQueryBuilder({ dateTimeUpTo: this.dateTime.upTo })
      this.urlQueryBuilder({ superPowers: JSON.stringify(this.superPowers) })
    },
    inputClean () {
      if (!this.term) {
        this.urlQueryBuilder({ term: null })
        this.urlQueryBuilder({ page: null })
      }
    },
    moreOptions () {
      this.isVisibleMoreOptions = !this.isVisibleMoreOptions
    },
    closeMoreOptions () {
      this.isVisibleMoreOptions = false
    },
    keyReadableForHuman (key) {
      return keyReadableForHuman(key)
    }
  },
  watch: {
    searchOnFieldsToSelect () {
      this.searchOnFieldsSelected = (() => {
        if (this.searchOnFieldsSelectedProp) {
          return this.searchOnFieldsSelectedProp
        } else {
          return this.searchOnFieldsToSelect.find(i => i.name)
            ? this.searchOnFieldsToSelect.map(i => i.name)
            : this.searchOnFieldsToSelect
        }
      })()
    },
    termProp () {
      this.term = this.termProp
    },
    dateFromProp () {
      this.dateTime.from = this.dateFromProp
    },
    dateUpToProp () {
      this.dateTime.upTo = this.dateUpToProp
    },
    lmSuperPowerSelectsV2 () {
      const superPowers = this.$route.query?.superPowers ? JSON.parse(this.$route.query.superPowers) : {}
      this.lmSuperPowerSelectsV2.forEach(lmSuperPowerSelectV2 => {
        const SPSkeyQueryString = lmSuperPowerSelectV2.keyQueryString
        console.log('SPSkeyQueryString', SPSkeyQueryString)
        console.log('superPowers[SPSkeyQueryString]', superPowers[SPSkeyQueryString])
        this.superPowerOptionsSelected[SPSkeyQueryString] = superPowers[SPSkeyQueryString]?.content
      })
    }
  }
}
</script>

<style lang="sass" scoped>
.app-search
  overflow: visible
  @media(max-width: $md)
    margin-top: 1.5rem !important
  .icon
    color: #727bf5
  .noti-icon-badge
    z-index: 7
    right: 5px
    top: 5px
    left: auto
  input
    border: 1px solid #ced4da !important
  .app-search__more-options-content
    position: absolute
    background: white
    z-index: $zIndexLmSearcher
    width: calc(100% - 30px)
    top: 38px
    box-shadow: 0 2px 4px rgba(0,0,0,0.3)
    border-radius: 0 0 0.25rem 0.25rem
</style>
